import { OTHER_DOMAIN, SKILL_DOMAINS, domainOf, type DomainOrOther } from "@/lib/skillDomains";
import { skillKey } from "@/lib/skills";
import type { TrackRecord } from "@/lib/types";

/**
 * Pure scoring for the profile track record. Everything here is derived from
 * `get_profile_track_record`'s counts — no I/O, no React — so the numbers a
 * profile shows are unit-testable and identical on /profile and /u/:handle.
 */

/**
 * 0–100 with diminishing returns: `100·(1 − e^(−n/k))`. `k` is the count at
 * which a track reads ~63. Volume alone approaches 100 but the fifth task moves
 * the needle far more than the fiftieth, so a newcomer's progress is visible.
 */
export function saturate(n: number, k: number): number {
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.round(100 * (1 - Math.exp(-n / k)));
}

export interface TrackScores {
  paid: number;
  volunteer: number;
  creator: number;
}

/** Paid and volunteer are separate tracks by design — neither discounts the other. */
export function computeScores(r: TrackRecord): TrackScores {
  return {
    paid: saturate(r.worker.paidTasks, 8),
    // An 8-hour volunteer day counts as much as one more task.
    volunteer: saturate(r.worker.volunteerTasks + r.worker.volunteerHours / 8, 6),
    // Delivered, not launched: a quest counts once someone else's work on it
    // was approved, so publishing empty quests moves nothing.
    creator: saturate(r.creator.questsDelivered + r.creator.tasksApproved / 2, 8),
  };
}

export type BadgeKind = "worker" | "creator" | "volunteer";
export type BadgeTier = "rising" | "prolific";
export interface Badge {
  kind: BadgeKind;
  tier: BadgeTier;
}

const BADGE_RULES: { kind: BadgeKind; value: (r: TrackRecord) => number; rising: number; prolific: number }[] = [
  { kind: "worker", value: (r) => r.worker.paidTasks + r.worker.volunteerTasks, rising: 3, prolific: 10 },
  { kind: "creator", value: (r) => r.creator.questsDelivered, rising: 3, prolific: 10 },
  { kind: "volunteer", value: (r) => r.worker.volunteerTasks, rising: 3, prolific: 5 },
];

export function computeBadges(r: TrackRecord): Badge[] {
  const out: Badge[] = [];
  for (const rule of BADGE_RULES) {
    const v = rule.value(r);
    if (v >= rule.prolific) out.push({ kind: rule.kind, tier: "prolific" });
    else if (v >= rule.rising) out.push({ kind: rule.kind, tier: "rising" });
  }
  return out;
}

export interface RadarAxis {
  domain: DomainOrOther;
  /** Approved tasks using a skill in this domain. */
  proven: number;
  /** Distinct profile skills in this domain. */
  claimed: number;
  /** 0–100 from proven work. */
  actual: number;
  /** 0–100 including claimed skills; always ≥ actual. */
  potential: number;
  /**
   * The skills behind the numbers, most-proven first, then claimed-only. Each
   * is the first spelling seen, so "ui-design" and "UI Design" appear once.
   */
  skills: string[];
}

interface SkillTally {
  label: string;
  proven: number;
  claimed: boolean;
}

/**
 * One axis per work domain, then "other" last for skills nothing recognises.
 * "Actual" is what approved work proves; "potential" adds what the profile
 * claims, so the gap between the two polygons is exactly the part of a
 * profile that isn't backed yet.
 */
export function computeRadar(r: TrackRecord, profileSkills: string[]): RadarAxis[] {
  const byDomain = new Map<DomainOrOther, Map<string, SkillTally>>();
  const tally = (skill: string, k: string): { domain: DomainOrOther; t: SkillTally } => {
    const domain = domainOf(skill);
    let skills = byDomain.get(domain);
    if (!skills) byDomain.set(domain, (skills = new Map()));
    let t = skills.get(k);
    if (!t) skills.set(k, (t = { label: skill.trim(), proven: 0, claimed: false }));
    return { domain, t };
  };

  // Proven = approved TASKS per domain. A React + TypeScript task is one task
  // of tech work, not two, so each task counts once per domain.
  const proven = new Map<DomainOrOther, number>();
  for (const skills of r.taskSkills) {
    const domains = new Set<DomainOrOther>();
    const keys = new Set<string>();
    for (const s of skills) {
      const k = skillKey(s);
      if (!k || keys.has(k)) continue;
      keys.add(k);
      const { domain, t } = tally(s, k);
      t.proven++;
      domains.add(domain);
    }
    for (const d of domains) proven.set(d, (proven.get(d) ?? 0) + 1);
  }
  for (const s of profileSkills) {
    const k = skillKey(s);
    if (k) tally(s, k).t.claimed = true;
  }

  const order: DomainOrOther[] = [...SKILL_DOMAINS, OTHER_DOMAIN];
  return order.map((domain) => {
    const tallies = [...(byDomain.get(domain)?.values() ?? [])];
    const p = proven.get(domain) ?? 0;
    const cl = tallies.filter((t) => t.claimed).length;
    const actual = saturate(p, 4);
    return {
      domain,
      proven: p,
      claimed: cl,
      actual,
      potential: Math.max(actual, saturate(p + 2 * cl, 4)),
      skills: tallies
        .sort((a, b) => b.proven - a.proven || Number(b.claimed) - Number(a.claimed))
        .map((t) => t.label),
    };
  });
}

/** Counts per track. `other` is work the RPC didn't split (pre-20260927150000). */
export interface ActivityCounts {
  paid: number;
  volunteer: number;
  launched: number;
  other: number;
}

export interface ActivityMonth extends ActivityCounts {
  /** First day of the UTC month, `YYYY-MM-01`. */
  start: string;
  total: number;
}

export interface MonthlyActivity {
  /** Oldest first; the last one is the current month. */
  months: ActivityMonth[];
  totals: ActivityCounts;
  total: number;
  /** The busiest month's total, the bar chart's full height (0 when empty). */
  max: number;
}

/**
 * A year of contributions as one bucket per UTC month (UTC to match the
 * RPC's day boundaries), ending with the current month. Days before the
 * first month are left out of every total.
 */
export function buildMonthlyActivity(
  activity: TrackRecord["activity"],
  today: Date,
  monthCount = 12,
): MonthlyActivity {
  const y = today.getUTCFullYear();
  const m = today.getUTCMonth();
  const months: ActivityMonth[] = Array.from({ length: monthCount }, (_, i) => ({
    start: new Date(Date.UTC(y, m - (monthCount - 1) + i, 1)).toISOString().slice(0, 10),
    paid: 0,
    volunteer: 0,
    launched: 0,
    other: 0,
    total: 0,
  }));
  const byMonth = new Map(months.map((mo) => [mo.start.slice(0, 7), mo]));
  const todayIso = today.toISOString().slice(0, 10);

  for (const a of activity) {
    const mo = a.day <= todayIso ? byMonth.get(a.day.slice(0, 7)) : undefined;
    if (!mo) continue;
    mo.paid += a.paid;
    mo.volunteer += a.volunteer;
    mo.launched += a.launched;
    mo.other += Math.max(0, a.worked - a.paid - a.volunteer);
  }

  const totals: ActivityCounts = { paid: 0, volunteer: 0, launched: 0, other: 0 };
  let max = 0;
  for (const mo of months) {
    mo.total = mo.paid + mo.volunteer + mo.launched + mo.other;
    totals.paid += mo.paid;
    totals.volunteer += mo.volunteer;
    totals.launched += mo.launched;
    totals.other += mo.other;
    max = Math.max(max, mo.total);
  }
  const total = totals.paid + totals.volunteer + totals.launched + totals.other;
  return { months, totals, total, max };
}
